<template>
  <v-dialog v-model="dialog" max-width="1400px" scrollable fullscreen-on-mobile>
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="dialog-title">
          <v-icon left>mdi-briefcase-account</v-icon>
          <span class="title-text">Uniform & Supply Assignments - {{ driver.name }}</span>
        </div>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pa-4">
        <div class="tabs-toolbar mb-4">
          <v-tabs v-model="activeTab">
            <v-tab>
              <v-icon left class="hidden-xs-only">mdi-clipboard-list</v-icon>
              <span class="hidden-xs-only">Active Assignments</span>
              <span class="hidden-sm-and-up">Active</span>
              <v-chip class="ml-2" small>{{ activeAssignments.length }}</v-chip>
            </v-tab>
            <v-tab>
              <v-icon left class="hidden-xs-only">mdi-clipboard-check</v-icon>
              <span class="hidden-xs-only">Returned Items</span>
              <span class="hidden-sm-and-up">Returned</span>
              <v-chip class="ml-2" small>{{ returnedAssignments.length }}</v-chip>
            </v-tab>
          </v-tabs>
          <v-btn color="primary" @click="openAssignItemDialog" class="assign-btn">
            <v-icon left class="hidden-xs-only">mdi-plus</v-icon>
            <span class="hidden-xs-only">Assign Item</span>
            <v-icon class="hidden-sm-and-up">mdi-plus</v-icon>
          </v-btn>
        </div>

        <v-tabs-items v-model="activeTab">
              <!-- Active Assignments Tab -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-data-table
                      :headers="activeHeaders"
                      :items="activeAssignments"
                      :loading="loading"
                      :options="{
                        sortBy: activeSort.sortBy,
                        sortDesc: activeSort.sortDesc,
                      }"
                      @update:options="updateActiveSort($event)"
                      :custom-sort="customSort"
                      no-data-text="No active assignments"
                      multi-sort
                    >
                      <template v-slot:[`item.itemName`]="{ item }">
                        <v-tooltip bottom v-if="item.itemName && item.itemName.length > 35" max-width="300">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="item-name-cell">
                              {{ item.itemName }}
                            </span>
                          </template>
                          <span class="tooltip-text">{{ item.itemName }}</span>
                        </v-tooltip>
                        <span v-else class="item-name-cell">{{ item.itemName || '-' }}</span>
                      </template>

                      <template v-slot:[`item.size`]="{ item }">
                        {{ item.size === 'NO_SIZE' ? '—' : formatItemSize(item.size) }}
                      </template>

                      <template v-slot:[`item.status`]="{ item }">
                        <v-chip
                          small
                          :color="getStatusColor(item.status)"
                          outlined
                        >
                          {{ getStatusLabel(item.status) }}
                        </v-chip>
                      </template>

                      <template v-slot:[`item.dateIssued`]="{ item }">
                        {{ formatDate(item.dateIssued) }}
                      </template>

                      <template v-slot:[`item.notes`]="{ item }">
                        <v-tooltip bottom v-if="item.notes && item.notes.length > 30" max-width="400">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="notes-cell">
                              {{ item.notes }}
                            </span>
                          </template>
                          <span class="tooltip-text">{{ item.notes }}</span>
                        </v-tooltip>
                        <span v-else class="notes-cell">{{ item.notes || '-' }}</span>
                      </template>

                      <template v-slot:[`item.actions`]="{ item }">
                        <v-btn
                          small
                          color="primary"
                          @click="openReturnItemDialog(item)"
                        >
                          Mark Returned
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-tab-item>

              <!-- Returned Items Tab -->
              <v-tab-item>
                <v-card flat>
                  <v-card-text>
                    <v-data-table
                      :headers="returnedHeaders"
                      :items="returnedAssignments"
                      :loading="loading"
                      :options="{
                        sortBy: returnedSort.sortBy,
                        sortDesc: returnedSort.sortDesc,
                      }"
                      @update:options="updateReturnedSort($event)"
                      :custom-sort="customSort"
                      no-data-text="No returned items"
                      multi-sort
                    >
                      <template v-slot:[`item.itemName`]="{ item }">
                        <v-tooltip bottom v-if="item.itemName && item.itemName.length > 35" max-width="300">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="item-name-cell">
                              {{ item.itemName }}
                            </span>
                          </template>
                          <span class="tooltip-text">{{ item.itemName }}</span>
                        </v-tooltip>
                        <span v-else class="item-name-cell">{{ item.itemName || '-' }}</span>
                      </template>

                      <template v-slot:[`item.size`]="{ item }">
                        {{ item.size === 'NO_SIZE' ? '—' : formatItemSize(item.size) }}
                      </template>

                      <template v-slot:[`item.assignmentInfo.dateIssued`]="{ item }">
                        {{ formatDate(item.assignmentInfo.dateIssued) }}
                      </template>

                      <template v-slot:[`item.dateReturned`]="{ item }">
                        {{ formatDate(item.dateReturned) }}
                      </template>

                      <template v-slot:[`item.returnToInventory`]="{ item }">
                        <v-icon v-if="item.returnToInventory" color="green">
                          mdi-check-circle
                        </v-icon>
                        <v-icon v-else color="grey">
                          mdi-close-circle
                        </v-icon>
                      </template>

                      <template v-slot:[`item.notes`]="{ item }">
                        <v-tooltip bottom v-if="item.notes && item.notes.length > 30" max-width="400">
                          <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="notes-cell">
                              {{ item.notes }}
                            </span>
                          </template>
                          <span class="tooltip-text">{{ item.notes }}</span>
                        </v-tooltip>
                        <span v-else class="notes-cell">{{ item.notes || '-' }}</span>
                      </template>
                    </v-data-table>
                  </v-card-text>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
      </v-card-text>
    </v-card>

    <AssignItemDialog ref="assignItemDialog" />
    <ReturnItemDialog ref="returnItemDialog" />
  </v-dialog>
</template>

<script>
import moment from "moment-timezone";
import { mapState } from "vuex";
import AssignItemDialog from "./AssignItemDialog.vue";
import ReturnItemDialog from "./ReturnItemDialog.vue";
import { formatItemSize } from "@/utils/utils";

export default {
  name: "DriverAssignments",

  components: {
    AssignItemDialog,
    ReturnItemDialog,
  },

  props: {
    driver: {
      type: Object,
      required: true,
    },
  },

  data: () => ({
    dialog: false,
    activeTab: 0,

    // Table sort settings
    activeSort: {
      sortBy: [],
      sortDesc: [],
    },
    returnedSort: {
      sortBy: [],
      sortDesc: [],
    },

    activeHeaders: [
      {
        text: "Item Name",
        value: "itemName",
        sortable: true,
      },
      {
        text: "Size",
        value: "size",
        sortable: true,
      },
      {
        text: "Quantity",
        value: "quantity",
        sortable: true,
      },
      {
        text: "Remaining",
        value: "remainingQuantity",
        sortable: false,
      },
      {
        text: "Status",
        value: "status",
        sortable: true,
      },
      {
        text: "Date Issued",
        value: "dateIssued",
        sortable: true,
      },
      {
        text: "Notes",
        value: "notes",
        sortable: false,
      },
      {
        text: "Actions",
        value: "actions",
        sortable: false,
        align: "center",
      },
    ],

    returnedHeaders: [
      {
        text: "Item Name",
        value: "itemName",
        sortable: true,
      },
      {
        text: "Size",
        value: "size",
        sortable: true,
      },
      {
        text: "Quantity",
        value: "quantity",
        sortable: true,
      },
      {
        text: "Date Issued",
        value: "assignmentInfo.dateIssued",
        sortable: true,
      },
      {
        text: "Date Returned",
        value: "dateReturned",
        sortable: true,
      },
      {
        text: "Returned to Inventory",
        value: "returnToInventory",
        sortable: true,
      },
      {
        text: "Notes",
        value: "notes",
        sortable: false,
      },
    ],

  }),

  computed: {
    ...mapState("assignmentModule", {
      activeAssignments: "activeAssignments",
      returnedAssignments: "returnedAssignments",
      loading: "loading",
    }),
    ...mapState("companyModule", ["company"]),
  },

  methods: {
    formatItemSize,

    // Custom sort function to prevent client-side sorting (server-side sorting only)
    customSort(items) {
      return items;
    },

    openDialog() {
      this.dialog = true;
      this.loadAssignments();
    },

    closeDialog() {
      this.dialog = false;
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).tz(this.company.timeZone).format("L");
    },

    getStatusColor(status) {
      switch (status) {
        case "ACTIVE":
          return "green";
        case "PARTIALLY_RETURNED":
          return "orange";
        case "RETURNED":
          return "grey";
        default:
          return "grey";
      }
    },

    getStatusLabel(status) {
      switch (status) {
        case "ACTIVE":
          return this.$localizationService.localize("store.assignment.status.active");
        case "PARTIALLY_RETURNED":
          return this.$localizationService.localize("store.assignment.status.partially_returned");
        case "RETURNED":
          return this.$localizationService.localize("store.assignment.status.returned");
        default:
          return status;
      }
    },

    openAssignItemDialog() {
      this.$refs.assignItemDialog.openDialog(this.driver.id);
    },

    openReturnItemDialog(item) {
      this.$refs.returnItemDialog.openDialog(item, this.driver.id);
    },

    async loadAssignments() {
      await Promise.all([
        this.loadActiveAssignments(),
        this.loadReturnedAssignments(),
      ]);
    },

    // Map frontend field names to backend field names for active assignments
    mapActiveFieldToBackend(field) {
      const fieldMap = {
        itemName: "inventory.itemName",
        size: "inventory.size",
        quantity: "quantity",
        status: "status",
        dateIssued: "dateIssued",
      };
      return fieldMap[field] || field;
    },

    // Map frontend field names to backend field names for returned assignments
    mapReturnedFieldToBackend(field) {
      const fieldMap = {
        itemName: "assignment.inventory.itemName",
        size: "assignment.inventory.size",
        quantity: "quantity",
        "assignmentInfo.dateIssued": "assignment.dateIssued",
        dateReturned: "dateReturned",
        returnToInventory: "returnToInventory",
      };
      return fieldMap[field] || field;
    },

    // Build sort array from v-data-table options (like in inventory page)
    buildSortArray(sortBy, sortDesc, fieldMapper) {
      if (!sortBy || sortBy.length === 0) {
        return [];
      }

      return sortBy.map((field, index) => {
        const backendField = fieldMapper(field);
        const direction = sortDesc[index] ? "desc" : "asc";
        return `${backendField}:${direction}`;
      });
    },

    updateActiveSort(data) {
      this.activeSort = {
        sortBy: [...data.sortBy],
        sortDesc: [...data.sortDesc],
      };

      const sort = this.buildSortArray(
        data.sortBy,
        data.sortDesc,
        this.mapActiveFieldToBackend,
      );

      this.loadActiveAssignments(sort);
    },

    updateReturnedSort(data) {
      this.returnedSort = {
        sortBy: [...data.sortBy],
        sortDesc: [...data.sortDesc],
      };

      const sort = this.buildSortArray(
        data.sortBy,
        data.sortDesc,
        this.mapReturnedFieldToBackend,
      );

      this.loadReturnedAssignments(sort);
    },

    async loadActiveAssignments(sort = []) {
      await this.$store.dispatch("assignmentModule/loadActiveAssignments", {
        driverId: this.driver.id,
        sort,
      });
    },

    async loadReturnedAssignments(sort = []) {
      await this.$store.dispatch("assignmentModule/loadReturnedAssignments", {
        driverId: this.driver.id,
        sort,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.v-card {
  box-shadow: none;
}

.item-name-cell {
  display: block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.notes-cell {
  display: block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.tooltip-text {
  white-space: normal;
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
}

// Hide number input arrows
::v-deep input[type="number"]::-webkit-inner-spin-button,
::v-deep input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep input[type="number"] {
  -moz-appearance: textfield;
}

// Fix table header height to prevent jumping
::v-deep .v-data-table thead th {
  height: 48px;
  white-space: nowrap;
}

::v-deep .v-data-table tbody td {
  height: 48px;
}

// Mobile responsive styles
@media (max-width: 599px) {
  .dialog-title {
    font-size: 14px;
    .title-text {
      display: none;
    }
  }

  .v-card-text {
    padding: 8px !important;
  }

  .tabs-toolbar {
    flex-direction: column;
    align-items: stretch !important;

    .v-tabs {
      margin-bottom: 8px;
    }

    .assign-btn {
      width: 100%;
    }
  }

  .item-name-cell {
    max-width: 150px;
  }

  .notes-cell {
    max-width: 120px;
  }

  .autocomplete-item-name {
    max-width: 200px;
  }

  .autocomplete-selected-item {
    max-width: 200px;
  }

  // Adjust table headers and cells for mobile
  ::v-deep .v-data-table {
    font-size: 12px;

    th, td {
      padding: 0 8px !important;
    }
  }
}

@media (min-width: 600px) {
  .tabs-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}
</style>
